// HRMS — shared UI primitives. Re-uses Finance Books style.
import React from 'react';

export const BSC_ORANGE = '#F28C28';
export const BSC_GREEN = '#1AA84B';
export const PANEL = 'bg-neutral-950 border border-white/10';
export const SOFT = 'bg-white/[0.03] border border-white/10';

export const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d); }
};
export const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(d); }
};
export const fmtMoney = (v, sym = '₹') => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return `${sym}${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const initials = (name) => (name || '').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

export function PageHeader({ title, subtitle, right, testid }) {
  return (
    <div className="flex items-start justify-between mb-6" data-testid={testid}>
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">{title}</h1>
        {subtitle && <p className="text-white/50 text-sm mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Kpi({ label, value, sub, tone = 'neutral', testid }) {
  const c = { neutral: 'text-white', green: 'text-emerald-400', red: 'text-rose-400',
              orange: 'text-orange-400', blue: 'text-sky-300' }[tone] || 'text-white';
  return (
    <div className={`${SOFT} rounded-xl p-5`} data-testid={testid}>
      <div className="text-[10px] tracking-[0.25em] text-white/45 uppercase">{label}</div>
      <div className={`mt-2 text-2xl font-semibold ${c}`}>{value}</div>
      {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

export function Btn({ children, variant = 'primary', className = '', ...rest }) {
  const v = {
    primary: 'bg-white text-neutral-900 hover:bg-white/90',
    ghost: 'bg-transparent text-white/80 hover:bg-white/5 border border-white/15',
    accent: 'text-neutral-900',
    danger: 'bg-rose-600 text-white hover:bg-rose-500',
  }[variant];
  const style = variant === 'accent' ? { backgroundColor: BSC_ORANGE } : {};
  return <button className={`px-3 py-2 rounded-lg text-sm font-medium transition ${v} ${className}`} style={style} {...rest}>{children}</button>;
}

export function Input({ label, ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-[10px] tracking-[0.2em] text-white/45 uppercase">{label}</span>}
      <input className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/30" {...rest} />
    </label>
  );
}
export function Select({ label, children, ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-[10px] tracking-[0.2em] text-white/45 uppercase">{label}</span>}
      <select className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-white/30" {...rest}>{children}</select>
    </label>
  );
}
export function Textarea({ label, ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-[10px] tracking-[0.2em] text-white/45 uppercase">{label}</span>}
      <textarea className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/30" rows={3} {...rest} />
    </label>
  );
}

export function Pill({ children, tone = 'neutral' }) {
  const cls = {
    neutral: 'bg-white/10 text-white/70',
    green: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    orange: 'bg-orange-500/15 text-orange-300 border border-orange-500/30',
    red: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
    blue: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
    grey: 'bg-white/5 text-white/50 border border-white/10',
  }[tone] || 'bg-white/10 text-white/70';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] tracking-[0.15em] uppercase font-medium ${cls}`}>{children}</span>;
}
export function statusTone(s) {
  if (!s) return 'neutral';
  s = String(s).toLowerCase();
  if (['active', 'present', 'approved', 'completed', 'finalized', 'hired', 'open', 'resolved'].includes(s)) return 'green';
  if (['draft', 'pending', 'in_progress', 'screening', 'interview', 'offered', 'applied', 'on_leave', 'probation'].includes(s)) return 'orange';
  if (['rejected', 'terminated', 'closed', 'void', 'absent', 'notice'].includes(s)) return 'red';
  return 'neutral';
}

export function Table({ columns, rows, empty = 'No records', testidPrefix = 'row' }) {
  return (
    <div className={`${PANEL} rounded-xl overflow-hidden`}>
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-white/55 text-[10px] tracking-[0.2em] uppercase">
          <tr>{columns.map((c, i) => (<th key={i} className={`px-4 py-3 text-left font-medium ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>))}</tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {!rows.length && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-white/35 text-sm italic">{empty}</td></tr>}
          {rows.map((row, i) => (
            <tr key={row.id || i} className="hover:bg-white/[0.02]" data-testid={`${testidPrefix}-${i}`}>
              {columns.map((c, j) => (<td key={j} className={`px-4 py-3 ${c.align === 'right' ? 'text-right tabular-nums' : ''} text-white/85`}>{c.render ? c.render(row) : row[c.key]}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Drawer({ open, onClose, title, children, width = 'max-w-3xl' }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="hr-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className={`w-full ${width} bg-neutral-950 border-l border-white/10 h-full overflow-y-auto`}>
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-white/10 bg-neutral-950 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none" data-testid="hr-drawer-close">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Avatar({ name, photo, size = 32, testid }) {
  const sz = `${size}px`;
  if (photo) return <img src={photo} alt={name} style={{ width: sz, height: sz }} className="rounded-full object-cover" data-testid={testid} />;
  return (
    <div style={{ width: sz, height: sz, fontSize: size/2.5 }}
      className="rounded-full bg-orange-500/20 text-orange-200 flex items-center justify-center font-medium"
      data-testid={testid}>
      {initials(name)}
    </div>
  );
}

export const HR_NAV = [
  { key: 'dashboard', label: 'Dashboard' },
  { kind: 'heading', label: 'People' },
  { key: 'employees', label: 'Employees' },
  { key: 'directory', label: 'Directory' },
  { key: 'org', label: 'Org Structure' },
  { kind: 'heading', label: 'Time' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'leaves', label: 'Leaves' },
  { key: 'holidays', label: 'Holiday Calendar' },
  { kind: 'heading', label: 'Talent' },
  { key: 'hiring', label: 'Hiring' },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'performance', label: 'Performance' },
  { kind: 'heading', label: 'Payroll' },
  { key: 'salary', label: 'Salary Structures' },
  { key: 'payroll', label: 'Payroll Runs' },
  { key: 'reimbursements', label: 'Reimbursements' },
  { kind: 'heading', label: 'Self Service' },
  { key: 'announcements', label: 'Announcements' },
  { key: 'helpdesk', label: 'HR Helpdesk' },
  { kind: 'heading', label: 'Setup' },
  { key: 'companies', label: 'Companies & Branches' },
  { key: 'settings', label: 'Configuration' },
];
